/*
 * Rail navigation: the console is an app shell that swaps #ViewContainer by
 * AJAX and keeps the browser URL, history and the selected rail entry in
 * step. Direct navigation and refresh land on _AppShell, which calls
 * bootstrapFromUrl to replay the same swap.
 */
import { ajaxSwap, ajaxModal } from './ajax';
import { one, all } from './dom';
import { parseDataValue } from './dispatch';
import { resolveActionSync } from './registry';
import { initTabs, showTab } from './tabs';
import { closeRail, toggleRail } from './rail';

export type NavCallback = (() => void) | null | undefined;

const NAV_LINK = '.mcp-nav-link';
const ANY_LINK = 'a[data-action="Home.TopMenu.NavigateFromLink"]';

function currentDomainId(): string {
    const input = document.getElementById('CurrentDomainId') as HTMLInputElement | null;
    const value = input?.value;
    // Coalesce to 0 (Global): '/undefined' misses the {domainId:int} route and
    // falls through to an unconstrained overload -- raw JSON in the view container.
    return value === undefined || value === null || value === '' ? '0' : value;
}

function pushHistory(fullUrl: string): void {
    try { window.history.pushState({ url: fullUrl }, '', fullUrl); } catch { /* sandboxed */ }
}

function clearSelected(): void {
    for (const link of all(`${NAV_LINK}, ${ANY_LINK}`)) link.classList.remove('selected');
    for (const group of all('.mcp-nav-group.has-selected')) group.classList.remove('has-selected');
}

function markSelected(link: Element): void {
    link.classList.add('selected');
    link.closest('.mcp-nav-group')?.classList.add('has-selected');
}

/** The rail link whose URL owns `fullUrl` (rail links first, then any menu link). */
function markSelectedByUrl(fullUrl: string): void {
    const path = fullUrl.split('?')[0] ?? fullUrl;
    const candidates = [...all(`${NAV_LINK}[data-action="Home.TopMenu.NavigateFromLink"]`), ...all(ANY_LINK)];
    for (const link of candidates) {
        const args = parseDataValue(link.getAttribute('data-args'));
        const argUrl = Array.isArray(args) && typeof args[0] === 'string' ? args[0] : null;
        if (argUrl && (path === argUrl || path.startsWith(`${argUrl}/`))) {
            markSelected(link);
            return;
        }
    }
}

/**
 * Swap #ViewContainer to the result of `url` (with the current domain id
 * appended unless told otherwise), push the URL onto history unless replaying
 * one, and mark the rail entry that owns it.
 */
export function getTopView(elem: Element | string | null, url: string, callback?: NavCallback, appendDomainId = true, skipHistory = false): Promise<void> {
    const fullUrl = url + (appendDomainId ? `/${currentDomainId()}` : '');
    clearSelected();
    closeRail();           // a drawer on a narrow screen closes as the page changes

    return ajaxSwap({
        container: 'ViewContainer',
        url: fullUrl,
        busy: '#TopMenu_BusyIndicator',
        onSuccess: () => {
            if (!skipHistory) pushHistory(fullUrl);
            callback?.();
        },
        onError: (_xhr, _status, error) => {
            const container = document.getElementById('ViewContainer');
            if (container) {
                container.replaceChildren();
                const p = document.createElement('p');
                const span = document.createElement('span');
                span.className = 'text-danger';
                span.textContent = `Error: Failed to render selected view: ${error}`;
                p.appendChild(span);
                container.append(document.createElement('br'), p);
            }
            return true;
        },
        onComplete: () => {
            const target = typeof elem === 'string' ? one(elem) : one(elem);
            // Only a rail link is worth marking directly; the brand, a dashboard
            // tile or a Refresh button resolve to the rail entry by URL.
            if (target && target.matches(NAV_LINK)) markSelected(target);
            else markSelectedByUrl(fullUrl);
        },
    });
}

function callbackOf(name: string | null | undefined): NavCallback {
    if (!name) return null;
    const handler = resolveActionSync(name);
    return handler ? () => { handler.call(document.body); } : null;
}

/** data-action target when the clicked element IS the menu link. */
export function navigateFromLink(this: Element, url: string, callbackName?: string | null, appendDomainId?: boolean): false {
    void getTopView(this, url, callbackOf(callbackName), appendDomainId ?? true);
    return false;
}

/** data-action target when another control should act as if a menu link was clicked. */
export function navigateFromSelector(this: Element, linkSelector: string, url: string, callbackName?: string | null, appendDomainId?: boolean): false {
    void getTopView(one(linkSelector), url, callbackOf(callbackName), appendDomainId ?? true);
    return false;
}

/** Re-run the current view for the newly selected domain. */
export function currentDomainChanged(): void {
    const selected = one(`${NAV_LINK}.selected`) as HTMLElement | null;
    if (selected) selected.click();
    else (document.getElementById('HomeDashboardLink') as HTMLElement | null)?.click();
}

/** Post-navigation hook for the Application area: wire lazy tabs, open People. */
export function appCallback(): void {
    initTabs();
    (document.getElementById('ApplicationPeopleLink') as HTMLElement | null)?.click();
}

export function showMyProfile(personId: number | string | null | undefined): false {
    if (!personId) return false;
    void ajaxModal({
        modal: 'GlobalModal',
        url: `Person/Detail/${personId}`,
        errorTitle: 'My Profile',
        errorMessage: 'Profile failed to load:',
        onSuccess: () => {
            initTabs();
            showTab('button.nav-link[data-tab="Properties"]');
        },
    });
    return false;
}

/** Called by _AppShell on direct navigation / refresh: replay the URL without re-pushing it. */
export function bootstrapFromUrl(fullUrl: string | null | undefined): void {
    if (!fullUrl) return;
    try { window.history.replaceState({ url: fullUrl }, '', fullUrl); } catch { /* sandboxed */ }
    void getTopView(null, fullUrl, null, false, true);
}

let bound = false;

/**
 * popstate (replay the stored URL without forking history) and the AppShell
 * hand-off.
 *
 * `mcp:appshell-bootstrap` is dispatched by Mcp.Core.Web's `_AppShell` view
 * (1.5.24+), which is what a non-AJAX GET of an `[AppShellNavigable]` action
 * returns: a chromed page with an empty #ViewContainer and a spinner. Until
 * something replays the URL over AJAX, that spinner is the whole page -- so
 * this listener is what makes deep links, pasted URLs and F5 work at all.
 *
 * The view called `window.Home.TopMenu.BootstrapFromUrl` before, out of a
 * jQuery ready wrapper. Audit Phase 3 removed both the global and jQuery, and
 * with no compiler, no test and no console error on the server side, all 39 of
 * those routes went to a permanent spinner in silence.
 */
export function initNav(): void {
    if (bound) return;
    bound = true;
    window.addEventListener('popstate', onPopState);
    document.addEventListener('mcp:appshell-bootstrap', onAppShellBootstrap);
}

function onPopState(event: PopStateEvent): void {
    const state = event.state as { url?: string } | null;
    if (!state?.url) return;
    void getTopView(null, state.url, null, false, true);
}

function onAppShellBootstrap(event: Event): void {
    const detail = (event as CustomEvent<{ url?: string } | null>).detail;
    bootstrapFromUrl(detail?.url);
}

/** Test support. Removes the listeners: re-running initNav would otherwise pair them. */
export function resetNav(): void {
    window.removeEventListener('popstate', onPopState);
    document.removeEventListener('mcp:appshell-bootstrap', onAppShellBootstrap);
    bound = false;
}

/** The Home.TopMenu handler tree, registered under "Home". */
export const TopMenu = {
    CurrentDomainChanged: currentDomainChanged,
    GetTopView: getTopView,
    AppCallback: appCallback,
    NavigateFromLink: navigateFromLink,
    NavigateFromSelector: navigateFromSelector,
    ShowMyProfile: showMyProfile,
    BootstrapFromUrl: bootstrapFromUrl,
    ToggleRail: toggleRail,
    CloseRail: closeRail,
    _pushHistory: pushHistory,
    _markSelectedByUrl: markSelectedByUrl,
};
